<template>
  <div class="bank-attachment-row">
    <div class="bank-attachment-row__list">
      <AttachmentList
        :attachments="attachmentsForType"
        :attachmentType="attachmentType"
        :deletable="!isBlockReadOnly"
        :hidesDeleteForGuests="!isOrganizationRoleNavigationEnabled"
        v-on:attachment-list:delete="onDelete"
      />
    </div>
    <div class="bank-attachment-row__upload">
      <DropzoneUpload
        v-if="!isBlockReadOnly"
        :complete="isUploadComplete"
        :parentId="project.id"
        :parentType="attachmentType"
        :uploadCallback="onUpload"
        :multiUpload="true"
        v-on:dropzone-upload:reset="isUploadComplete = false"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import DropzoneUpload from '@/components/DropzoneUpload';
import AttachmentList from '@/components/AttachmentList';
import { AccessBlockMixin } from '@/mixins/accessBlock';

/**
 * Bank documents of one type: list, upload and delete. Inside a bank block, read-only hides
 * upload and delete, and in Matrix-Authz the level decides delete instead of the guest rule.
 * Emits `changed` after an upload or a delete, so the block reloads itself; outside a block
 * (SalesEdit) nobody listens.
 */
export default {
  name: 'BankAttachmentUpload',
  components: {
    DropzoneUpload,
    AttachmentList,
  },
  mixins: [AccessBlockMixin],
  props: {
    project: {
      type: Object,
      required: true,
    },
    attachmentType: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isUploadComplete: false,
    };
  },
  computed: {
    ...mapGetters('auth', ['isOrganizationRoleNavigationEnabled']),
    attachmentsForType() {
      const bankAttachments = this.project?.bank?.attachments;
      if (!bankAttachments) {
        return [];
      }
      return bankAttachments.filter(
        (attachment) => attachment.attachmentType === this.attachmentType
      );
    },
  },
  methods: {
    ...mapActions(['uploadProjectDynamicAttachment', 'deleteProjectDynamicAttachment']),

    async onUpload(payload) {
      for (const file of payload.files) {
        await this.uploadProjectDynamicAttachment({
          file,
          parentId: payload.parentId,
          parentType: payload.parentType,
        });
      }
      this.$emit('changed');
    },

    async onDelete(event) {
      await this.deleteProjectDynamicAttachment({
        projectId: this.project.id,
        attachmentId: event.attachmentId,
      });
      this.$emit('changed');
    },
  },
  mounted() {
    this.$store.subscribeAction({
      after: (action) => {
        if (
          action.type === 'uploadProjectDynamicAttachment' &&
          action.payload?.parentType === this.attachmentType
        ) {
          this.isUploadComplete = true;
        }
      },
    });
  },
};
</script>

<style scoped lang="scss">
.bank-attachment-row {
  display: grid;
  grid-template-columns: minmax(0, 6fr) minmax(0, 4fr);
  gap: 1rem;
  align-items: start;
  min-width: 0;
}

.bank-attachment-row__list {
  min-width: 0;
}

.bank-attachment-row__upload {
  min-width: 0;
}

@media (max-width: 992px) {
  .bank-attachment-row {
    grid-template-columns: 1fr;
  }
}
</style>
